import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Tag,
  ChevronDown,
  Building,
  Users,
  User,
  X,
  Search,
  Copy,
  CheckCircle,
  Clock
} from 'lucide-react';
import api from '../utils/api';

const VariablePicker = ({ 
  onVariableSelect, 
  clientId = null, 
  contactId = null,
  buttonClassName = "p-1 text-gray-400 hover:text-gray-600 transition-colors",
  dropdownClassName = "",
  showPreview = true,
  placeholder = "Insert variable..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [variables, setVariables] = useState({
    datetime: [],
    tenant: [],
    client: [],
    contact: [],
    project: [],
    task: [],
    portal: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewValues, setPreviewValues] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadVariables();
      updateDropdownPosition();
    }
  }, [isOpen, clientId, contactId]);

  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 400; // Estimated max height of dropdown
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Decide whether to show above or below based on available space
      const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      
      let top, left;
      
      if (showAbove) {
        // Position above the button
        top = rect.top - dropdownHeight - 4;
      } else {
        // Position below the button
        top = rect.bottom + 4;
      }
      
      // Adjust left position if dropdown would go off-screen
      left = rect.left;
      if (left + 320 > window.innerWidth) { // 320px = w-80
        left = window.innerWidth - 320 - 10;
      }
      
      setDropdownPosition({ top, left });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadVariables = async () => {
    setLoading(true);
    
    // Define date/time variables that should always be available
    const dateTimeVars = [
      { name: 'Current Date', variable: '{{datetime.today}}', description: 'Today\'s date (MM/DD/YYYY)', type: 'datetime', hardcoded: true },
      { name: 'Current Date (Long)', variable: '{{datetime.today_long}}', description: 'Today\'s date (Monday, January 1, 2024)', type: 'datetime', hardcoded: true },
      { name: 'Current Date (Short)', variable: '{{datetime.today_short}}', description: 'Today\'s date (Jan 1, 2024)', type: 'datetime', hardcoded: true },
      { name: 'Current Time', variable: '{{datetime.current_time}}', description: 'Current time (12-hour format, e.g., 3:45 PM)', type: 'datetime', hardcoded: true },
      { name: 'Current Time (24hr)', variable: '{{datetime.current_time_24}}', description: 'Current time (24-hour format, e.g., 15:45)', type: 'datetime', hardcoded: true },
      { name: 'Current Year', variable: '{{datetime.year}}', description: 'Current year (e.g., 2024)', type: 'datetime', hardcoded: true },
      { name: 'Current Month', variable: '{{datetime.month}}', description: 'Current month name (e.g., January)', type: 'datetime', hardcoded: true },
      { name: 'Current Month Number', variable: '{{datetime.month_number}}', description: 'Current month number (e.g., 01)', type: 'datetime', hardcoded: true },
      { name: 'Current Day', variable: '{{datetime.day}}', description: 'Current day of month (e.g., 15)', type: 'datetime', hardcoded: true },
      { name: 'Current Weekday', variable: '{{datetime.weekday}}', description: 'Current day of week (e.g., Monday)', type: 'datetime', hardcoded: true },
      { name: 'Date + Time', variable: '{{datetime.now}}', description: 'Current date and time (MM/DD/YYYY at 3:45 PM)', type: 'datetime', hardcoded: true }
    ];
    
    // Define hardcoded variables that should always be available
    const tenantHardcodedVars = [
      { name: 'Organization Name', variable: '{{tenant.company_name}}', description: 'Your organization name', type: 'tenant', hardcoded: true },
      { name: 'Organization Website', variable: '{{tenant.website}}', description: 'Your organization website', type: 'tenant', hardcoded: true },
      { name: 'Organization Email', variable: '{{tenant.email}}', description: 'Your organization email', type: 'tenant', hardcoded: true },
      { name: 'Organization Phone', variable: '{{tenant.phone}}', description: 'Your organization phone number', type: 'tenant', hardcoded: true },
      { name: 'Organization Address', variable: '{{tenant.address}}', description: 'Your organization address', type: 'tenant', hardcoded: true },
      { name: 'Support Email', variable: '{{tenant.support_email}}', description: 'Your support email address', type: 'tenant', hardcoded: true },
      { name: 'Sales Email', variable: '{{tenant.sales_email}}', description: 'Your sales email address', type: 'tenant', hardcoded: true }
    ];

    const clientHardcodedVars = [
      { name: 'Client Name', variable: '{{client.name}}', description: 'Client company name', type: 'client', hardcoded: true },
      { name: 'Client Company', variable: '{{client.company}}', description: 'Client company name (alias)', type: 'client', hardcoded: true },
      { name: 'Client Email', variable: '{{client.email}}', description: 'Primary client email', type: 'client', hardcoded: true },
      { name: 'Client Phone', variable: '{{client.phone}}', description: 'Primary client phone', type: 'client', hardcoded: true },
      { name: 'Client Website', variable: '{{client.website}}', description: 'Client website URL', type: 'client', hardcoded: true },
      { name: 'Client Industry', variable: '{{client.industry}}', description: 'Client industry type', type: 'client', hardcoded: true },
      { name: 'Client Type', variable: '{{client.client_type}}', description: 'Type of client (Lead, Prospect, Active, etc.)', type: 'client', hardcoded: true },
      { name: 'Client Address', variable: '{{client.address}}', description: 'Client physical address', type: 'client', hardcoded: true },
      { name: 'Client City', variable: '{{client.city}}', description: 'Client city', type: 'client', hardcoded: true },
      { name: 'Client State', variable: '{{client.state}}', description: 'Client state/province', type: 'client', hardcoded: true },
      { name: 'Client Zip', variable: '{{client.zip}}', description: 'Client zip/postal code', type: 'client', hardcoded: true },
      { name: 'Client Country', variable: '{{client.country}}', description: 'Client country', type: 'client', hardcoded: true }
    ];

    const contactHardcodedVars = [
      { name: 'Contact Name', variable: '{{contact.name}}', description: 'Contact full name', type: 'contact', hardcoded: true },
      { name: 'Contact First Name', variable: '{{contact.first_name}}', description: 'Contact first name', type: 'contact', hardcoded: true },
      { name: 'Contact Last Name', variable: '{{contact.last_name}}', description: 'Contact last name', type: 'contact', hardcoded: true },
      { name: 'Contact Email', variable: '{{contact.email}}', description: 'Contact email address', type: 'contact', hardcoded: true },
      { name: 'Contact Phone', variable: '{{contact.phone}}', description: 'Contact phone number', type: 'contact', hardcoded: true },
      { name: 'Contact Mobile', variable: '{{contact.mobile}}', description: 'Contact mobile phone', type: 'contact', hardcoded: true },
      { name: 'Contact Job Title', variable: '{{contact.job_title}}', description: 'Contact job title/position', type: 'contact', hardcoded: true },
      { name: 'Contact Department', variable: '{{contact.department}}', description: 'Contact department', type: 'contact', hardcoded: true },
      { name: 'Contact Company', variable: '{{contact.company}}', description: 'Contact company name', type: 'contact', hardcoded: true },
      { name: 'Contact Address', variable: '{{contact.address}}', description: 'Contact address', type: 'contact', hardcoded: true },
      { name: 'Contact LinkedIn', variable: '{{contact.linkedin}}', description: 'Contact LinkedIn profile', type: 'contact', hardcoded: true },
      { name: 'Contact Notes', variable: '{{contact.notes}}', description: 'Contact notes/bio', type: 'contact', hardcoded: true }
    ];

    const projectHardcodedVars = [
      { name: 'Project Name', variable: '{{project.name}}', description: 'Project name', type: 'project', hardcoded: true },
      { name: 'Project Status', variable: '{{project.status}}', description: 'Project status', type: 'project', hardcoded: true },
      { name: 'Project Category', variable: '{{project.category}}', description: 'Project category', type: 'project', hardcoded: true }
    ];

    const taskHardcodedVars = [
      { name: 'Task Name', variable: '{{task.name}}', description: 'Task title/name', type: 'task', hardcoded: true },
      { name: 'Task Description', variable: '{{task.description}}', description: 'Task description/details', type: 'task', hardcoded: true },
      { name: 'Task Status', variable: '{{task.status}}', description: 'Current task status (To Do, In Progress, Done)', type: 'task', hardcoded: true },
      { name: 'Task Priority', variable: '{{task.priority}}', description: 'Task priority level (Low, Normal, High, Critical)', type: 'task', hardcoded: true },
      { name: 'Task Due Date', variable: '{{task.due_date}}', description: 'Task due date', type: 'task', hardcoded: true },
      { name: 'Task Assignee', variable: '{{task.assignee}}', description: 'Person assigned to the task', type: 'task', hardcoded: true },
      { name: 'Task Review Notes', variable: '{{task.review_notes}}', description: 'Execution team feedback and review comments', type: 'task', hardcoded: true },
      { name: 'Task Deliverables', variable: '{{task.deliverables}}', description: 'List of task deliverable files and links', type: 'task', hardcoded: true },
      { name: 'Task Attachments', variable: '{{task.attachments}}', description: 'Task uploads/attachments', type: 'task', hardcoded: true }
    ];

    const portalHardcodedVars = [
      { name: 'Portal Request Type', variable: '{{portal.request_type}}', description: 'Portal submission request type', type: 'portal', hardcoded: true },
      { name: 'Portal Title', variable: '{{portal.title}}', description: 'Portal submission title', type: 'portal', hardcoded: true },
      { name: 'Portal Description', variable: '{{portal.description}}', description: 'Portal submission description', type: 'portal', hardcoded: true },
      { name: 'Portal Priority', variable: '{{portal.priority}}', description: 'Portal submission priority', type: 'portal', hardcoded: true },
      { name: 'Portal Attachments Count', variable: '{{portal.attachments_count}}', description: 'Portal submission attachment count', type: 'portal', hardcoded: true },
      { name: 'Portal Reference Tables', variable: '{{portal.reference_tables}}', description: 'Reference table ids/names used by submission', type: 'portal', hardcoded: true },
      { name: 'Portal Reference Table Fields', variable: '{{portal.reference_table_fields}}', description: 'Submitted reference-table-backed field values', type: 'portal', hardcoded: true }
    ];

    // Initialize with hardcoded variables
    let tenantVars = [...tenantHardcodedVars];
    let clientVars = [...clientHardcodedVars];
    let contactVars = [...contactHardcodedVars];
    let projectVars = [...projectHardcodedVars];
    let taskVars = [...taskHardcodedVars];
    let portalVars = [...portalHardcodedVars];

    try {
      // Try to load custom variables and add them to hardcoded ones
      const [tenantResponse, clientDefsResponse, projectDefsResponse, taskDefsResponse, contactDefsResponse, portalFieldsResponse] = await Promise.all([
        api.get('/custom-values'),
        api.get('/custom-field-definitions?entity_type=client'),
        api.get('/custom-field-definitions?entity_type=project'),
        api.get('/custom-field-definitions?entity_type=task'),
        api.get('/contact-custom-field-definitions'),
        api.get('/custom-values?category=portal_field')
      ]);

      // Add custom tenant variables
      const tenantCustomVars = tenantResponse.data.map(value => ({
        id: value.id,
        name: value.name,
        variable: `{{tenant.${value.variable_name}}}`,
        value: value.value,
        description: value.description,
        type: 'tenant',
        hardcoded: false
      }));
      tenantVars.push(...tenantCustomVars);

      // Add custom client variables
      const clientCustomVars = clientDefsResponse.data.map(def => ({
        id: def.id,
        name: def.label,
        variable: `{{client.${def.variable_name}}}`,
        description: def.description,
        type: 'client',
        fieldType: def.type,
        required: def.required,
        hardcoded: false
      }));
      clientVars.push(...clientCustomVars);

      // Add custom project variables
      const projectCustomVars = projectDefsResponse.data.map(def => ({
        id: def.id,
        name: def.label,
        variable: `{{project.${def.variable_name}}}`,
        description: def.description,
        type: 'project',
        fieldType: def.type,
        required: def.required,
        hardcoded: false
      }));
      projectVars.push(...projectCustomVars);

      // Add custom task variables
      const taskCustomVars = taskDefsResponse.data.map(def => ({
        id: def.id,
        name: def.label,
        variable: `{{custom_field.${def.name || def.variable_name}}}`,
        description: def.description,
        type: 'task',
        fieldType: def.type,
        required: def.required,
        hardcoded: false
      }));
      taskVars.push(...taskCustomVars);

      // Add custom contact variables
      const contactCustomVars = contactDefsResponse.data.map(def => ({
        id: def.id,
        name: def.label,
        variable: `{{contact.${def.variable_name}}}`,
        description: def.description,
        type: 'contact',
        fieldType: def.type,
        required: def.required,
        hardcoded: false
      }));
      contactVars.push(...contactCustomVars);

      // Add portal field variables
      const portalFieldVars = portalFieldsResponse.data.map(field => ({
        id: field.id,
        name: field.name,
        variable: field.variable_name?.startsWith('portal.') ? `{{${field.variable_name}}}` : `{{portal.field.${field.variable_name}}}`,
        description: field.description || `Portal form field: ${field.name}`,
        type: 'portal',
        fieldType: field.field_type,
        required: field.is_required,
        hardcoded: false,
        isPortalField: true
      }));
      portalVars.push(...portalFieldVars);

    } catch (error) {
      console.error('Error loading custom variables (using hardcoded only):', error);
      // Continue with just hardcoded variables if API fails
    }

    // Always set variables (at minimum, the hardcoded ones)
    setVariables({
      datetime: dateTimeVars,
      tenant: tenantVars,
      client: clientVars,
      contact: contactVars,
      project: projectVars,
      task: taskVars,
      portal: portalVars
    });

    // Load preview values if context is provided
    if (showPreview && (clientId || contactId)) {
      try {
        await loadPreviewValues();
      } catch (error) {
        console.error('Error loading preview values:', error);
      }
    }

    setLoading(false);
  };

  const loadPreviewValues = async () => {
    try {
      const previewData = {};
      
      // Add datetime preview values (always available)
      const now = new Date();
      const options_long = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      const options_short = { year: 'numeric', month: 'short', day: 'numeric' };
      
      previewData['{{datetime.today}}'] = now.toLocaleDateString('en-US');
      previewData['{{datetime.today_long}}'] = now.toLocaleDateString('en-US', options_long);
      previewData['{{datetime.today_short}}'] = now.toLocaleDateString('en-US', options_short);
      previewData['{{datetime.current_time}}'] = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      previewData['{{datetime.current_time_24}}'] = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      previewData['{{datetime.year}}'] = now.getFullYear().toString();
      previewData['{{datetime.month}}'] = now.toLocaleDateString('en-US', { month: 'long' });
      previewData['{{datetime.month_number}}'] = String(now.getMonth() + 1).padStart(2, '0');
      previewData['{{datetime.day}}'] = now.getDate().toString();
      previewData['{{datetime.weekday}}'] = now.toLocaleDateString('en-US', { weekday: 'long' });
      previewData['{{datetime.now}}'] = `${now.toLocaleDateString('en-US')} at ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
      
      if (clientId) {
        // Get client data
        const clientResponse = await api.get(`/clients/${clientId}`);
        
        if (clientResponse.data) {
          const client = clientResponse.data;
          // Map hardcoded client variables
          previewData['{{client.name}}'] = client.name || client.company;
          previewData['{{client.company}}'] = client.company || client.name;
          previewData['{{client.email}}'] = client.email;
          previewData['{{client.phone}}'] = client.phone;
          previewData['{{client.website}}'] = client.website;
          previewData['{{client.industry}}'] = client.industry;
          previewData['{{client.client_type}}'] = client.client_type;
          previewData['{{client.address}}'] = client.address;
          previewData['{{client.city}}'] = client.city;
          previewData['{{client.state}}'] = client.state;
          previewData['{{client.zip}}'] = client.zip;
          previewData['{{client.country}}'] = client.country;
        }

        // Get client custom field values
        try {
          const clientFieldsResponse = await api.get(`/clients/${clientId}/custom-fields`);
          
          if (clientFieldsResponse.data && clientFieldsResponse.data.custom_fields) {
            clientFieldsResponse.data.custom_fields.forEach(field => {
              if (field.value && field.definition) {
                const variable = `{{client.${field.definition.variable_name}}}`;
                previewData[variable] = field.value.value;
              }
            });
          }
        } catch (error) {
          console.log('No client custom fields found');
        }
      }

      if (contactId) {
        // Get contact data from client's contacts array or standalone contacts
        try {
          let contact = null;
          
          // First try to get contact from client data if clientId is available
          if (clientId) {
            const clientResponse = await api.get(`/clients/${clientId}`);
            
            if (clientResponse.data && clientResponse.data.contacts) {
              contact = clientResponse.data.contacts.find(c => c.id === contactId);
            }
          }
          
          if (contact) {
            // Map hardcoded contact variables
            previewData['{{contact.name}}'] = contact.name;
            previewData['{{contact.first_name}}'] = contact.first_name || contact.name?.split(' ')[0];
            previewData['{{contact.last_name}}'] = contact.last_name || contact.name?.split(' ').slice(1).join(' ');
            previewData['{{contact.email}}'] = contact.email;
            previewData['{{contact.phone}}'] = contact.phone;
            previewData['{{contact.mobile}}'] = contact.mobile;
            previewData['{{contact.job_title}}'] = contact.job_title;
            previewData['{{contact.department}}'] = contact.department;
            previewData['{{contact.company}}'] = contact.company;
            previewData['{{contact.address}}'] = contact.address;
            previewData['{{contact.linkedin}}'] = contact.linkedin;
            previewData['{{contact.notes}}'] = contact.notes;
          }
          
          // Get contact custom field values
          const contactResponse = await api.get(`/contacts/${contactId}/custom-fields`);
          
          if (contactResponse.data && contactResponse.data.custom_fields) {
            contactResponse.data.custom_fields.forEach(field => {
              if (field.value && field.definition) {
                const variable = `{{contact.${field.definition.variable_name}}}`;
                previewData[variable] = field.value.value;
              }
            });
          }
        } catch (error) {
          console.log('No contact data found');
        }
      }

      // For tenant variables, we would need to fetch tenant/organization data
      // For now, we'll leave tenant preview values empty since we don't have a tenant data endpoint
      
      setPreviewValues(previewData);
    } catch (error) {
      console.error('Error loading preview values:', error);
    }
  };

  const handleVariableSelect = (variable) => {
    onVariableSelect(variable.variable);
    setIsOpen(false);
    setSearchTerm('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const filterVariables = (vars) => {
    if (!searchTerm) return vars;
    return vars.filter(variable => 
      variable.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      variable.variable.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (variable.description && variable.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const getVariableIcon = (type) => {
    switch (type) {
      case 'tenant': return <Building className="w-3 h-3" />;
      case 'client': return <Users className="w-3 h-3" />;
      case 'contact': return <User className="w-3 h-3" />;
      default: return <Tag className="w-3 h-3" />;
    }
  };

  const getVariableColor = (type) => {
    switch (type) {
      case 'tenant': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'client': return 'text-green-600 bg-green-50 border-green-200';
      case 'contact': return 'text-purple-600 bg-purple-50 border-purple-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const hasVariables =
    variables.datetime.length > 0 ||
    variables.tenant.length > 0 ||
    variables.client.length > 0 ||
    variables.contact.length > 0 ||
    variables.project.length > 0 ||
    variables.task.length > 0 ||
    variables.portal.length > 0;

  const dropdownContent = isOpen && (
    <div 
      ref={dropdownRef}
      className={`fixed w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-[99999] max-h-96 overflow-hidden ${dropdownClassName}`}
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
      }}
    >
          {/* Header */}
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">Insert Variable</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={placeholder}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Content */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-2 text-sm">Loading variables...</p>
              </div>
            ) : !hasVariables ? (
              <div className="p-4 text-center text-gray-500">
                <Tag className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm">No variables available</p>
                <p className="text-xs mt-1">Create custom values and field definitions first</p>
              </div>
            ) : (
              <div className="p-2 space-y-3">
                {/* Date/Time Variables */}
                {filterVariables(variables.datetime).length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 rounded">
                      <Clock className="w-3 h-3" />
                      <span>Date & Time Variables</span>
                    </div>
                    <div className="mt-1 space-y-1">
                      {filterVariables(variables.datetime).map((variable) => (
                        <div
                          key={variable.variable}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer group"
                          onClick={() => handleVariableSelect(variable)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {variable.name}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <code className="text-xs bg-indigo-100 text-indigo-700 px-1 rounded font-mono">
                                {variable.variable}
                              </code>
                              {showPreview && previewValues[variable.variable] && (
                                <span className="text-xs text-gray-500 truncate">
                                  → {previewValues[variable.variable]}
                                </span>
                              )}
                            </div>
                            {variable.description && (
                              <p className="text-xs text-gray-500 mt-1 truncate">{variable.description}</p>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(variable.variable);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-opacity"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tenant Variables */}
                {filterVariables(variables.tenant).length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded">
                      <Building className="w-3 h-3" />
                      <span>Organization Variables</span>
                    </div>
                    <div className="mt-1 space-y-1">
                      {filterVariables(variables.tenant).map((variable) => (
                        <div
                          key={variable.id || variable.variable}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer group"
                          onClick={() => handleVariableSelect(variable)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {variable.name}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <code className="text-xs bg-blue-100 text-blue-700 px-1 rounded font-mono">
                                {variable.variable}
                              </code>
                              {showPreview && previewValues[variable.variable] && (
                                <span className="text-xs text-gray-500 truncate">
                                  → {previewValues[variable.variable]}
                                </span>
                              )}
                            </div>
                            {variable.description && (
                              <p className="text-xs text-gray-500 mt-1 truncate">{variable.description}</p>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(variable.variable);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-opacity"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Client Variables */}
                {filterVariables(variables.client).length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded">
                      <Users className="w-3 h-3" />
                      <span>Client Variables</span>
                    </div>
                    <div className="mt-1 space-y-1">
                      {filterVariables(variables.client).map((variable, index) => (
                        <div
                          key={variable.id || variable.variable || index}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer group"
                          onClick={() => handleVariableSelect(variable)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {variable.name}
                              </span>
                              {variable.required && (
                                <span className="text-xs text-red-600">*</span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <code className="text-xs bg-green-100 text-green-700 px-1 rounded font-mono">
                                {variable.variable}
                              </code>
                              {showPreview && previewValues[variable.variable] && (
                                <span className="text-xs text-gray-500 truncate">
                                  → {previewValues[variable.variable]}
                                </span>
                              )}
                            </div>
                            {variable.description && (
                              <p className="text-xs text-gray-500 mt-1 truncate">{variable.description}</p>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(variable.variable);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-opacity"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Variables */}
                {filterVariables(variables.contact).length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 rounded">
                      <User className="w-3 h-3" />
                      <span>Contact Variables</span>
                    </div>
                    <div className="mt-1 space-y-1">
                      {filterVariables(variables.contact).map((variable, index) => (
                        <div
                          key={variable.id || variable.variable || index}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer group"
                          onClick={() => handleVariableSelect(variable)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {variable.name}
                              </span>
                              {variable.required && (
                                <span className="text-xs text-red-600">*</span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <code className="text-xs bg-purple-100 text-purple-700 px-1 rounded font-mono">
                                {variable.variable}
                              </code>
                              {showPreview && previewValues[variable.variable] && (
                                <span className="text-xs text-gray-500 truncate">
                                  → {previewValues[variable.variable]}
                                </span>
                              )}
                            </div>
                            {variable.description && (
                              <p className="text-xs text-gray-500 mt-1 truncate">{variable.description}</p>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(variable.variable);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-opacity"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Project Variables */}
                {filterVariables(variables.project).length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 px-2 py-1 text-xs font-medium text-cyan-700 bg-cyan-50 rounded">
                      <Building className="w-3 h-3" />
                      <span>Project Variables</span>
                    </div>
                    <div className="mt-1 space-y-1">
                      {filterVariables(variables.project).map((variable, index) => (
                        <div
                          key={variable.id || variable.variable || index}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer group"
                          onClick={() => handleVariableSelect(variable)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900 truncate">{variable.name}</span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <code className="text-xs bg-cyan-100 text-cyan-700 px-1 rounded font-mono">{variable.variable}</code>
                            </div>
                            {variable.description && <p className="text-xs text-gray-500 mt-1 truncate">{variable.description}</p>}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(variable.variable);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-opacity"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Portal Variables */}
                {filterVariables(variables.portal).length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 px-2 py-1 text-xs font-medium text-fuchsia-700 bg-fuchsia-50 rounded">
                      <Tag className="w-3 h-3" />
                      <span>Portal Variables</span>
                    </div>
                    <div className="mt-1 space-y-1">
                      {filterVariables(variables.portal).map((variable, index) => (
                        <div
                          key={variable.id || variable.variable || index}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer group"
                          onClick={() => handleVariableSelect(variable)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900 truncate">{variable.name}</span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <code className="text-xs bg-fuchsia-100 text-fuchsia-700 px-1 rounded font-mono">{variable.variable}</code>
                            </div>
                            {variable.description && <p className="text-xs text-gray-500 mt-1 truncate">{variable.description}</p>}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(variable.variable);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-opacity"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Task Variables */}
                {filterVariables(variables.task).length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 px-2 py-1 text-xs font-medium text-orange-700 bg-orange-50 rounded">
                      <CheckCircle className="w-3 h-3" />
                      <span>Task Variables</span>
                    </div>
                    <div className="mt-1 space-y-1">
                      {filterVariables(variables.task).map((variable, index) => (
                        <div
                          key={variable.id || variable.variable || index}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer group"
                          onClick={() => handleVariableSelect(variable)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {variable.name}
                              </span>
                              {variable.hardcoded && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  System
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {variable.variable}
                            </div>
                            {variable.description && (
                              <div className="text-xs text-gray-400 truncate">
                                {variable.description}
                              </div>
                            )}
                            {showPreview && previewValues[variable.variable] && (
                              <div className="text-xs text-blue-600 truncate">
                                Preview: {previewValues[variable.variable]}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(variable.variable);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-opacity"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-600">
            Click a variable to insert it, or use the copy button to copy to clipboard
          </p>
        </div>
      </div>
  );

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClassName}
        title="Insert variable"
      >
        <Tag className="w-4 h-4" />
      </button>

      {/* Dropdown Portal */}
      {isOpen && typeof document !== 'undefined' && ReactDOM.createPortal(
        dropdownContent,
        document.body
      )}
    </>
  );
};

export default VariablePicker;